import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  useTheme,
  alpha
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import UndoIcon from '@mui/icons-material/Undo';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { regionSyncService } from '../services/RegionSyncService';
import { SyncHistory, SyncDetail } from '../types';
import { logger } from 'utils/logger';

const log = logger.getLogger('SyncDetailDialog');

interface SyncDetailDialogProps {
  open: boolean;
  history: SyncHistory | null;
  onClose: () => void;
}

const SyncDetailDialog: React.FC<SyncDetailDialogProps> = ({
  open,
  history,
  onClose
}) => {
  const theme = useTheme();
  const [detail, setDetail] = useState<SyncDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const fetchDetail = useCallback(async (id: number | string) => {
    try {
      setLoading(true);
      setError(null);
      const data = await regionSyncService.getHistoryDetail(id);
      setDetail(data as SyncDetail);
    } catch (err: any) {
      log.error('Failed to fetch sync detail:', err);
      setError(err.message || 'Failed to load full details');
      // Fall back to summary data
      if (history) {
        setDetail(history as SyncDetail);
      }
    } finally {
      setLoading(false);
    }
  }, [history]);

  useEffect(() => {
    if (open && history) {
      fetchDetail(history.syncId || history.id);
    } else {
      setDetail(null);
      setError(null);
    }
  }, [open, history, fetchDetail]);

  const handleCopyId = () => {
    if (history) {
      navigator.clipboard.writeText(String(history.id));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const getStatusColor = (status: string): 'success' | 'error' | 'warning' | 'info' | 'default' => {
    const s = (status || '').toUpperCase();
    switch (s) {
      case 'COMPLETED':
        return 'success';
      case 'FAILED':
        return 'error';
      case 'RUNNING':
      case 'IN_PROGRESS':
        return 'info';
      case 'ROLLED_BACK':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    const s = (status || '').toUpperCase();
    switch (s) {
      case 'COMPLETED':
        return <CheckCircleIcon fontSize="small" />;
      case 'FAILED':
        return <ErrorIcon fontSize="small" />;
      case 'RUNNING':
      case 'IN_PROGRESS':
        return <PlayCircleIcon fontSize="small" />;
      case 'ROLLED_BACK':
        return <UndoIcon fontSize="small" />;
      default:
        return <HourglassEmptyIcon fontSize="small" />;
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '—';
    return normalizeUtcDate(dateString).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short'
    });
  };

  const normalizeUtcDate = (dateString: string): Date => {
    let normalized = dateString;
    if (!/[Z]$/i.test(dateString) && !/[+-]\d{2}:?\d{2}$/.test(dateString)) {
      normalized = dateString.replace(' ', 'T') + 'Z';
    }
    return new Date(normalized);
  };

  const formatDuration = (start: string, end?: string) => {
    if (!end) return 'In progress';
    const diffMs = normalizeUtcDate(end).getTime() - normalizeUtcDate(start).getTime();
    const diffSecs = Math.floor(diffMs / 1000);
    if (diffSecs < 60) return `${diffSecs}s`;
    const diffMins = Math.floor(diffSecs / 60);
    if (diffMins < 60) return `${diffMins}m ${diffSecs % 60}s`;
    const diffHours = Math.floor(diffMins / 60);
    return `${diffHours}h ${diffMins % 60}m`;
  };

  // Detail table header style (matching the main table header pattern)
  const detailHeaderCellStyle = {
    fontWeight: 600,
    fontSize: '0.8rem',
    textTransform: 'uppercase',
    letterSpacing: '0.025em',
    py: 1,
    borderBottom: `2px solid ${alpha(theme.palette.divider, 0.7)}`,
    backgroundColor: theme.palette.background.default,
  };

  const displayData = detail || history;

  if (!displayData) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <InfoOutlinedIcon color="primary" sx={{ fontSize: '1.8rem' }} />
          <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
            Sync Operation Details
          </Typography>
          <Chip
            icon={getStatusIcon(displayData.status)}
            label={displayData.status}
            size="small"
            color={getStatusColor(displayData.status)}
            sx={{
              fontWeight: 500,
              borderRadius: '8px'
            }}
          />
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            transition: 'all 0.2s',
            '&:hover': {
              backgroundColor: alpha(theme.palette.error.main, 0.08),
              color: theme.palette.error.main
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ py: 3 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {error && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Sync Name & ID */}
        <Box sx={{ mb: 3 }}>
          {displayData.syncName && (
            <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 600, color: theme.palette.primary.main }}>
              {displayData.syncName}
            </Typography>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Sync ID:
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
              {displayData.id}
            </Typography>
            <Tooltip title={copied ? 'Copied!' : 'Copy ID'}>
              <IconButton
                size="small"
                onClick={handleCopyId}
                sx={{
                  transition: 'all 0.2s',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    transform: 'scale(1.1)'
                  }
                }}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Source → Target */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Box
              sx={{
                p: 2,
                backgroundColor: alpha(theme.palette.primary.main, 0.04),
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                transition: 'all 0.2s',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.06)
                }
              }}
            >
              <Typography variant="subtitle2" color="primary" gutterBottom sx={{ fontWeight: 600 }}>
                Source
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                Account {displayData.sourceAccountId || displayData.accountId}
              </Typography>
              {displayData.sourceConfigId && (
                <Typography variant="caption" color="text.secondary" display="block">
                  Config #{displayData.sourceConfigId}
                </Typography>
              )}
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Box
              sx={{
                p: 2,
                backgroundColor: alpha(theme.palette.secondary.main, 0.04),
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`,
                transition: 'all 0.2s',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.secondary.main, 0.06)
                }
              }}
            >
              <Typography variant="subtitle2" color="secondary" gutterBottom sx={{ fontWeight: 600 }}>
                Target
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                Account {displayData.targetAccountId || displayData.accountId}
              </Typography>
              {displayData.targetConfigId && (
                <Typography variant="caption" color="text.secondary" display="block">
                  Config #{displayData.targetConfigId}
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>

        {/* Key Details */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 6, md: 3 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.025em' }}>
              Sync Type
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              <Chip label={displayData.syncType} size="small" variant="outlined" sx={{ fontWeight: 500, borderRadius: '8px' }} />
            </Box>
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.025em' }}>
              Menu ID
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              {displayData.menuId || '—'}
            </Typography>
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.025em' }}>
              Tables Synced
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              {(displayData.tablesSynced || []).length}
            </Typography>
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.025em' }}>
              Triggered By
            </Typography>
            <Typography variant="body2" fontWeight={500}>{displayData.triggeredBy || '—'}</Typography>
          </Grid>
        </Grid>

        {/* Timeline */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 6, md: 4 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.025em' }}>
              Started
            </Typography>
            <Typography variant="body2">{formatDate(displayData.startedAt)}</Typography>
          </Grid>
          <Grid size={{ xs: 6, md: 4 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.025em' }}>
              Completed
            </Typography>
            <Typography variant="body2">{formatDate(displayData.completedAt)}</Typography>
          </Grid>
          <Grid size={{ xs: 6, md: 4 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.025em' }}>
              Duration
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              {formatDuration(displayData.startedAt, displayData.completedAt)}
            </Typography>
          </Grid>
        </Grid>

        {/* Per-Table Breakdown */}
        {detail?.recordsByTable && Object.keys(detail.recordsByTable).length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              Records by Table
            </Typography>
            <TableContainer sx={{ borderRadius: 1, border: `1px solid ${alpha(theme.palette.divider, 0.3)}` }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={detailHeaderCellStyle}>Table</TableCell>
                    <TableCell align="right" sx={detailHeaderCellStyle}>Inserted</TableCell>
                    <TableCell align="right" sx={detailHeaderCellStyle}>Updated</TableCell>
                    <TableCell align="right" sx={detailHeaderCellStyle}>Skipped</TableCell>
                    <TableCell align="right" sx={detailHeaderCellStyle}>Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody
                  sx={{
                    '& .MuiTableRow-root:nth-of-type(odd)': {
                      backgroundColor: alpha(theme.palette.background.default, 0.4),
                    }
                  }}
                >
                  {Object.entries(detail.recordsByTable).map(([tableName, counts]) => (
                    <TableRow
                      key={tableName}
                      hover
                      sx={{
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: `${alpha(theme.palette.primary.light, 0.08)} !important`
                        }
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
                          {tableName}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {counts.inserted > 0 ? (
                          <Chip label={counts.inserted} size="small" color="success" variant="outlined" sx={{ fontWeight: 500, borderRadius: '8px' }} />
                        ) : (
                          <Typography variant="body2" color="text.secondary">0</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {counts.updated > 0 ? (
                          <Chip label={counts.updated} size="small" color="warning" variant="outlined" sx={{ fontWeight: 500, borderRadius: '8px' }} />
                        ) : (
                          <Typography variant="body2" color="text.secondary">0</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {counts.skipped > 0 ? (
                          <Typography variant="body2" color="info.main" fontWeight={500}>
                            {counts.skipped}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">0</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={500}>
                          {counts.inserted + counts.updated + counts.skipped}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                  {/* Summary row */}
                  <TableRow sx={{ '& td': { fontWeight: 600, borderTop: 2, borderColor: 'divider' } }}>
                    <TableCell>Total</TableCell>
                    <TableCell align="right">
                      {Object.values(detail.recordsByTable).reduce((sum, c) => sum + c.inserted, 0)}
                    </TableCell>
                    <TableCell align="right">
                      {Object.values(detail.recordsByTable).reduce((sum, c) => sum + c.updated, 0)}
                    </TableCell>
                    <TableCell align="right">
                      {Object.values(detail.recordsByTable).reduce((sum, c) => sum + c.skipped, 0)}
                    </TableCell>
                    <TableCell align="right">
                      {(displayData.recordsProcessed ?? 0).toLocaleString()}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* Rollback Information */}
        {detail?.revertedAt && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ p: 2, backgroundColor: alpha(theme.palette.warning.main, 0.08), borderRadius: 2, border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}` }}>
              <Typography variant="subtitle2" color="warning.main" gutterBottom sx={{ fontWeight: 600 }}>
                Rollback Information
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 6, md: 4 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>Reverted At</Typography>
                  <Typography variant="body2">{formatDate(detail.revertedAt)}</Typography>
                </Grid>
                <Grid size={{ xs: 6, md: 4 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>Reverted By</Typography>
                  <Typography variant="body2">{detail.revertedBy || '—'}</Typography>
                </Grid>
                <Grid size={{ xs: 6, md: 4 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>Revert Status</Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip
                      label={detail.revertStatus || 'completed'}
                      size="small"
                      color={detail.revertStatus === 'failed' ? 'error' : 'success'}
                      variant="outlined"
                      sx={{ fontWeight: 500, borderRadius: '8px' }}
                    />
                  </Box>
                </Grid>
              </Grid>
              {detail.revertError && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {detail.revertError}
                </Alert>
              )}
            </Box>
          </>
        )}

        {/* Rollback available hint */}
        {!detail?.revertedAt && displayData.rollbackAvailable && (displayData.status === 'completed' || displayData.status === 'COMPLETED') && (
          <>
            <Divider sx={{ my: 2 }} />
            <Alert severity="info">
              Rollback is available for this sync operation. Use the rollback button in the history
              table to restore the target database to its previous state.
            </Alert>
          </>
        )}

        {/* Error Information */}
        {displayData.status === 'failed' && displayData.errors && displayData.errors.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ p: 2, backgroundColor: alpha(theme.palette.error.main, 0.08), borderRadius: 2, border: `1px solid ${alpha(theme.palette.error.main, 0.2)}` }}>
              <Typography variant="subtitle2" color="error" gutterBottom sx={{ fontWeight: 600 }}>
                Error Information
              </Typography>
              {displayData.errors.map((err, index) => (
                <Alert key={index} severity="error" sx={{ mb: 1 }}>
                  {err}
                </Alert>
              ))}
              {detail?.errorDetails && (
                <Box sx={{ mt: 1, p: 1, backgroundColor: alpha(theme.palette.error.main, 0.04), borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>Stack Trace</Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                      whiteSpace: 'pre-wrap',
                      maxHeight: 200,
                      overflow: 'auto',
                      mt: 0.5
                    }}
                  >
                    {detail.errorDetails}
                  </Typography>
                </Box>
              )}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            textTransform: 'none',
            minWidth: '80px',
            fontWeight: 600,
            borderRadius: '12px',
            transition: 'all 0.2s',
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SyncDetailDialog;
